/**
 * v6 migration notes:
 *  - `match.path` doesn't exist anymore (no `match` prop is passed to route
 *    components in v6). This file is mounted at `/signup/*` in App.js, so
 *    the base path is fixed — hardcoded as SIGNUP_BASE_PATH below rather
 *    than relying on v6's relative-path resolution, to keep `goToForm` /
 *    `goToReferralForm` / the redirect target behaving identically to the
 *    original regardless of how this gets mounted in the future.
 *  - `history.push(x)` -> `navigate(x)` (useNavigate hook).
 *  - `Switch` -> `Routes`; `<Route exact path={x}>children</Route>` ->
 *    `<Route path={relativeX} element={children} />`. Paths are now
 *    relative to this component's own mount point (no more manual
 *    `path + '/x'` string-building for the Route `path` prop itself — only
 *    needed for the imperative navigate() targets, which stay absolute).
 *  - `<Redirect to={x}>` -> `<Navigate to={x} replace>`.
 *  - Fixed `connect(injectIntl)(SignUp)`: this was passing `injectIntl` (a
 *    HOC) into the slot `connect()` expects a `mapStateToProps` function in
 *    — `intl` was never actually being injected. `intl` isn't used in this
 *    file currently, so it was silently harmless, but it's fixed properly
 *    here (now `connect()(injectIntl(SignUp))`) since it would break the
 *    moment someone adds an intl.formatMessage call.
 */
import React, { useEffect, useState } from 'react';

import PropTypes from 'prop-types';
import { injectIntl } from 'react-intl';
import { connect } from 'react-redux';
import { Navigate, Route, Routes, useNavigate } from 'react-router-dom';

import {
  AppWrapper,
  AppWrapperHeading,
  Block,
  ContentWrapper,
} from 'components';
import {
  AuthorizedRedirect,
  SignupCouponBuilder,
  SignUpForm,
} from 'containers';
import { actions as referralCodesActions } from 'coupon-common/src/modules/referralCodes';
// TODO: confirm real signup action, e.g.:
// import { actions as authActions } from 'coupon-common/src/modules/auth';
// import { dispatchAsAsync } from 'coupon-common/src/utils';

import BaseLogo from '../../components/Logo/BaseLogo.svg';
import HeaderLogo from '../../components/Logo/HeaderLogo.svg';
import LocationSearchPage from '../Authentication/LocationSearch';
import BusinessDetailsPage from '../Authentication/BusinessDetails';
import MediaUploadPage from '../Authentication/MediaUpload';

const SIGNUP_BASE_PATH = '/signup/business';

export const SignUp = ({ intl, dispatch }) => {
  const navigate = useNavigate();
  const [isSignUpFlowActive, setIsSignUpFlowActive] = useState(false);
  const [selectedBusiness, setSelectedBusiness] = useState(null);
  const [businessDetails, setBusinessDetails] = useState(null);

  const admin_referral_code = process.env.REACT_APP_ADMIN_REFERRAL_CODE;

  const [defaultRefCode] = useState(admin_referral_code || '');

  useEffect(() => {
    dispatch(referralCodesActions.setBusinessCode(defaultRefCode));
  }, [defaultRefCode, dispatch]);

  const goToReferralForm = () => {
    navigate(`${SIGNUP_BASE_PATH}/referral-code`);
  };

  const goToForm = () => {
    navigate(`${SIGNUP_BASE_PATH}/form`);
  };

  const goToMyBusinessAccount = () => {
    navigate('/account');
  };

  return (
    <Routes>
      {/* Signup form */}
      <Route
        path="form"
        element={
          <>
            <AuthorizedRedirect signUpFlowActive={isSignUpFlowActive} />
            <AppWrapper
              showHeader
              showHeadingRow
              contentCentered
              isSignUpFlowActive={isSignUpFlowActive}
            >
              <Block className="w-100 p-0">
                <div className="row w-100 m-0">
                  {/* Left Section (Image) */}
                  <div
                    className="col-md-4 d-flex justify-content-center align-items-center"
                    style={{
                      backgroundColor: '#ff5252',
                    }}
                  >
                    <img
                      src="/MyCouponbook.webp"
                      alt="Signup Illustration"
                      style={{
                        width: '200px',
                        maxWidth: '80%',
                        margin: '10px',
                        borderRadius: '10px',
                      }}
                    />
                  </div>

                  {/* Right Section (Form) */}
                  <div className="col-md-8 d-flex justify-content-center align-items-center p-2">
                    <SignUpForm
                      onSubmitSuccess={goToMyBusinessAccount}
                      referralFormRedirect={goToReferralForm}
                      setSignUpFlowActive={setIsSignUpFlowActive}
                    />
                  </div>
                </div>
              </Block>
            </AppWrapper>
          </>
        }
      />

      <Route
        path="coupon-builder"
        element={
          <AppWrapper
            showHeader
            showHeadingRow
            contentCentered
            isSignUpFlowActive
            headingRowRender={() => (
              <AppWrapperHeading offsetLeft headingText="Create Your First Coupon" />
            )}
          >
            <ContentWrapper>
              <SignupCouponBuilder
                entryPoint="A"
                onRequireAuth={goToForm}
                onGoToFullBuilder={() => navigate('/coupons/create')}
              />
            </ContentWrapper>
          </AppWrapper>
        }
      />

      {/* New business signup flow */}
      <Route
        path="location"
        element={
          <>
            <AuthorizedRedirect signUpFlowActive={isSignUpFlowActive} />
            <LocationSearchPage
              onSelectBusiness={business => {
                setSelectedBusiness(business);
                navigate('business');
              }}
            />
          </>
        }
      />

      <Route
        path="business"
        element={
          <BusinessDetailsPage
            prefill={selectedBusiness}
            onNext={formValues => {
              setBusinessDetails(formValues);
              navigate('media');
            }}
          />
        }
      />

      <Route
        path="media"
        element={
          <MediaUploadPage
            onRegister={async ({ logoFile, bannerFile }) => {
              // TODO: replace with real signup dispatch, e.g.:
              // await dispatchAsAsync(
              //   authActions.signUp({
              //     ...businessDetails,
              //     referralCode: defaultRefCode,
              //     logoFile,
              //     bannerFile,
              //   }),
              //   dispatch,
              // );
              goToMyBusinessAccount();
            }}
          />
        }
      />

      <Route path="*" element={<Navigate to={`${SIGNUP_BASE_PATH}/form`} replace />} />
    </Routes>
  );
};

SignUp.propTypes = {
  intl: PropTypes.object,
  dispatch: PropTypes.func,
};

export default connect()(injectIntl(SignUp));